import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, IntlShape } from 'react-intl';
import React from 'react';
import { DefaultColumnValues, IConnectionsDAO, ICsvColumn } from '../../../../../api/connections.dao';
import { IEventsDAO } from '../../../../../api/events.dao';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { SHORT_PLAIN_TEXT_50_LENGTH } from '../../../../../constants/common.constants';
import { IConnectionExportTemplate, IUsersDAO } from '../../../../../api/users.dao';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { MixPanelTrackingEventMapper } from './mixpanel-tracking-events.mapper';
import { TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';

export const getDefaultCvsColumns = (): ICsvColumn[] => {
    const defaultEnums = Object.values(DefaultColumnValues);
    return defaultEnums.map((item: DefaultColumnValues, index: number) => ({
        position: index,
        included: true,
        header: item,
    }));
};

export default class AllEventConfigurationVM {
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public allColumns: ICsvColumn[] = [];
    @mobx.observable
    public configurationName: string = '';
    @mobx.observable
    public isConfigurationNameTouched: boolean = false;

    @mobx.observable
    public addedConfigurationIds: Set<number> = new Set<number>() ;
    @mobx.observable
    public removedConfigurationIds: Set<number> = new Set<number>() ;
    @mobx.observable
    public selectedConfigurationId: number;

    @mobx.observable
    public configurationTemplates: IConnectionExportTemplate[] = [];


    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        public onCancel: () => void,
        private readonly intl: IntlShape,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly alertsService : IAlertsService = IoC.Container.get(IAlertsService),
        private readonly eventsDAO: IEventsDAO = IoC.Container.get<IEventsDAO>(IEventsDAO),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly connectionsDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {}

    public getConfigurationNameError(): string | null {
        if (!this.configurationName) {
            return this.intl.formatMessage({
                id: `oe.common.nameIsRequired`,
                defaultMessage: 'Configuration Name is required',
                description: 'Error message in user popover'
            });
        } else if (this.configurationName.length > SHORT_PLAIN_TEXT_50_LENGTH) {
            return this.intl.formatMessage({
                id: `oe.validation.shortPlainText50TooLong`,
                defaultMessage: 'Too long - max length is 50 characters',
                description: 'Error message text'
            });
        } else if (this.configurationName.includes('/') || this.configurationName.includes('\\')) {
            return this.intl.formatMessage({
                id: `oe.validation.slashesNotAllowed`,
                defaultMessage: 'Slashes are not allowed',
                description: 'Error message text'
            });
        }

        return null;
    }

    @mobx.action
    public setConfigurationName = ({ target: { value } }) => {
        this.configurationName = value;
        this.isConfigurationNameTouched = true;
    };

    public onExportCSV = async () => {
        try {
            const columnsToExport = this.allColumns.map(c => ({
                header: c.header,
                position: c.position,
                included: c.included,
            }));

            await this.connectionsDAO.getAllEventConnectionsCSV({
                accountId: this.accountId,
                eventId: this.eventId,
                columns: columnsToExport,
            });

            this.trackDownloadAllEventConnections(columnsToExport);

        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        }
        return true;
    };

    private trackDownloadAllEventConnections(columns: ICsvColumn[]): void {
        const trackingData = {
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': this.eventId,
            ...MixPanelTrackingEventMapper.connectionCsvColumnsToTrackingEvent(columns)
        };
        void this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_ALL_EVENTS_CONNECTIONS, trackingData);
    }

    @mobx.action
    public setColumns(columns: ICsvColumn[]): void {
        this.allColumns = columns;
    }

    @mobx.action
    public updateOrder(columns: ICsvColumn[]) {
        let position = 0;
        const updatedColumns = columns.map(column => ({
            ...column,
            position: position++
        }));

        this.setColumns(updatedColumns);
    }

    @mobx.action
    public setCsvColumns = async () => {
        try {
            const columnsTemplates = await this.usersDAO.getConnectionExportTemplates(undefined);
            const reversedColumnsTemplates = columnsTemplates.reverse();
            this.configurationTemplates = reversedColumnsTemplates;
            const defaultColumns = reversedColumnsTemplates[0]?.columns ?? getDefaultCvsColumns();
            defaultColumns.sort((a, b) => a.position - b.position);
            this.allColumns = defaultColumns;
            if (reversedColumnsTemplates[0]) {
                this.selectedConfigurationId = reversedColumnsTemplates[0].connectionExportTemplateId;
            }

        } catch (e) {
            mobx.runInAction(() => {
                const errorMessage = this.errorParsingService.extractMessage(e);
                alertServerError('Error', errorMessage);
            });
        }
    };

    @mobx.action
    public saveConfiguration = async () => {
        this.isConfigurationNameTouched = true;
        if (!this.getConfigurationNameError()) {

            try {
                const columnsTemplate = await this.usersDAO.createConnectionExportTemplate({
                    name: this.configurationName.trim(),
                    columns: this.allColumns,
                });


                mobx.runInAction(() => {
                    this.configurationTemplates.unshift(columnsTemplate);
                    this.configurationName = '';
                    this.isConfigurationNameTouched = false;
                    this.addedConfigurationIds.add( columnsTemplate.connectionExportTemplateId);
                });

                setTimeout(() => {
                    mobx.runInAction(async () => {
                        this.addedConfigurationIds.delete( columnsTemplate.connectionExportTemplateId);
                    });

                }, 3000);


            } catch (e) {
                mobx.runInAction(() => {
                    const errorMessage = this.errorParsingService.extractMessage(e);
                    alertServerError('Error', errorMessage);
                });
            }
        }
    };

    @mobx.action
    public loadConfiguration = (templateId: number) => {

        const selectedConfiguration = this.configurationTemplates.find(({connectionExportTemplateId}) => connectionExportTemplateId === templateId);
        this.selectedConfigurationId = templateId;
        this.allColumns = selectedConfiguration.columns;
    };

    public onDelete = (templateId: number) => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        description='Warning message'
                        defaultMessage='Are you sure you want to'
                        id='oe.warnings.areYouSureTo'
                     />
                    <span className='alert-message--danger'> <FormattedMessage
                        description='Warning message'
                    defaultMessage='delete this configuration'
                    id='oe.allEventConnectionCsv.deleteThisConfiguration'
                    />
                    </span>?
                 </span>
            ),
            confirmButtonProps: {
            text: (
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Delete Configuration'
                        id='oe.allEventConnectionCsv.deleteConfiguration'
                    />
                ),
                modifier: 'confirm-warn',
                handler: () => this.deleteConfiguration(templateId),
        },
        cancelButtonProps: {
            modifier: 'cancel'
        }
    });
        return false;
    };

    @mobx.action
    public deleteConfiguration = async (templateId: number) => {
        if (this.removedConfigurationIds.has(templateId)) {
            return;
        }
        try {
            await this.usersDAO.deleteConnectionExportTemplates(templateId);

            mobx.runInAction(async () => {
                this.removedConfigurationIds.add(templateId);
            });

            setTimeout(() => {
                mobx.runInAction(async () => {
                    this.configurationTemplates = this.configurationTemplates.filter(({connectionExportTemplateId}) => {
                        return connectionExportTemplateId !== templateId;

                    } );
                    this.removedConfigurationIds.delete(templateId);
                });

            }, 3000);

        } catch (e) {
            mobx.runInAction(() => {
                const errorMessage = this.errorParsingService.extractMessage(e);
                alertServerError('Error', errorMessage);
            });
        }
    };

}
